import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  ChevronDownIcon,
  XMarkIcon,
  CheckIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

interface DropdownItem {
  value: string;
  label: string;
  icon?: React.ReactNode;
  subLabel?: string;
  disabled?: boolean;
  [key: string]: any; // Allow additional properties
}

interface DropdownProps {
  items: DropdownItem[];
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  type?: "single" | "multi";
  value?: string | string[]; // Current selected value(s)
  onChange: (value: string | string[]) => void;
  disabled?: boolean;
  loading?: boolean;
  required?: boolean;
  error?: string;
  className?: string;
  maxHeight?: string;
  searchable?: boolean; // Optional search functionality
  searchFields?: string[]; // Fields to search in (default: ['label', 'subLabel'])
  usePortal?: boolean; // Render menu in a portal (useful inside tables)
  showClear?: boolean; // Show clear (X) button when there is a selection
  creatable?: boolean; // Allow creating new items (requires searchable)
  onCreate?: (value: string) => void; // Callback when new item is created
  hideChips?: boolean; // Hide internal chips in multi-select (show count text instead)
  fitContent?: boolean; // Size trigger to content width
}

const Dropdown: React.FC<DropdownProps> = ({
  items,
  label,
  placeholder,
  searchPlaceholder,
  type = "single",
  value,
  onChange,
  disabled = false,
  loading = false,
  required = false,
  error,
  className = "",
  maxHeight = "300px",
  searchable = false,
  searchFields = ["label", "subLabel"],
  usePortal = true,
  showClear = true,
  creatable = false,
  onCreate,
  hideChips = false,
  fitContent = false,
}) => {
  const resolvedPlaceholder = placeholder ?? "Select option";
  const resolvedSearchPlaceholder = searchPlaceholder ?? "Search";
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [portalStyle, setPortalStyle] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight?: string;
  } | null>(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Convert value to array for consistent handling
  const selectedValues: string[] =
    type === "multi"
      ? Array.isArray(value)
        ? value
        : []
      : value && typeof value === "string"
      ? [value]
      : [];
  const selectedSingleItem =
    type === "single" && selectedValues.length === 1
      ? items.find((item) => item.value === selectedValues[0]) || null
      : null;
  const isDisabled = disabled || loading;

  // Filter items based on search term
  const filteredItems = items.filter((item) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return searchFields.some((field) => {
      const fieldValue = item[field];
      if (fieldValue && typeof fieldValue === "string") {
        return fieldValue.toLowerCase().includes(searchLower);
      }
      return false;
    });
  });

  // Get display text for selected items
  const getDisplayText = () => {
    if (selectedValues.length === 0) {
      return resolvedPlaceholder;
    }

    if (type === "single") {
      const selectedItem = items.find(
        (item) => item.value === selectedValues[0]
      );
      return selectedItem ? selectedItem.label : resolvedPlaceholder;
    }

    // Multi-select display
    if (selectedValues.length === 1) {
      const selectedItem = items.find(
        (item) => item.value === selectedValues[0]
      );
      return selectedItem ? selectedItem.label : resolvedPlaceholder;
    }

    return `${selectedValues.length} items selected`;
  };

  // Handle item selection
  const handleItemClick = (item: DropdownItem) => {
    if (item.disabled) {
      return;
    }

    const itemValue = item.value;
    if (type === "single") {
      onChange(itemValue);
      setIsOpen(false);
      setSearchTerm("");
    } else {
      // Multi-select logic
      const newValues = selectedValues.includes(itemValue)
        ? selectedValues.filter((v) => v !== itemValue)
        : [...selectedValues, itemValue];
      onChange(newValues as string[]);
    }
  };

  // Handle remove item (for multi-select)
  const handleRemoveItem = (itemValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (type === "multi") {
      const newValues = selectedValues.filter((v) => v !== itemValue);
      onChange(newValues as string[]);
    }
  };

  // Handle clear all
  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(type === "multi" ? [] : "");
    setSearchTerm("");
  };

  // Handle create new item (for creatable mode)
  const handleCreateItem = () => {
    if (!creatable || !searchTerm.trim()) return;
    
    const newValue = searchTerm.trim();
    
    // Call onCreate callback if provided
    if (onCreate) {
      onCreate(newValue);
    }
    
    // For multi-select, add the new value to selection
    if (type === "multi") {
      const newValues = [...selectedValues, newValue];
      onChange(newValues as string[]);
    } else {
      // For single select, just set the value
      onChange(newValue);
      setIsOpen(false);
    }
    
    setSearchTerm("");
    setHighlightedIndex(0);
  };

  // Handle keyboard navigation for search
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => 
        Math.min(prev + 1, filteredItems.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      // If we have filtered items and something is highlighted, select it
      if (filteredItems.length > 0 && highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
        handleItemClick(filteredItems[highlightedIndex]);
      } else if (creatable && searchTerm.trim()) {
        // If no items match and creatable, create new item
        handleCreateItem();
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  // Reset highlighted index to first item when search term changes
  useEffect(() => {
    // When there's a search term and filtered items exist, highlight the first one
    // This ensures pressing Enter will select the first matching item
    if (searchTerm && filteredItems.length > 0) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [searchTerm, filteredItems.length]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInsideDropdown = dropdownRef.current?.contains(target);
      const clickedInsideMenu = menuRef.current?.contains(target);
      if (!clickedInsideDropdown && !clickedInsideMenu) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Compute portal position when opening or on resize/scroll with auto-flip
  useEffect(() => {
    if (!usePortal || !isOpen) return;
    const updatePosition = () => {
      const el = dropdownRef.current;
      const menu = menuRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Estimate dropdown height (or use actual if menu exists)
      const estimatedDropdownHeight = menu?.offsetHeight || 300;
      
      // Decide whether to flip upward
      const shouldFlip = spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow;
      setIsFlipped(shouldFlip);
      
      if (shouldFlip) {
        // Position above
        setPortalStyle({
          bottom: viewportHeight - rect.top + 4,
          left: rect.left,
          width: rect.width,
          maxHeight: `${Math.min(spaceAbove - 20, parseInt(maxHeight))}px`,
        });
      } else {
        // Position below (default)
        setPortalStyle({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
          maxHeight: maxHeight,
        });
      }
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [usePortal, isOpen, maxHeight]);

  // Detect flip for non-portal mode
  useEffect(() => {
    if (usePortal || !isOpen) return;
    const updateFlip = () => {
      const el = dropdownRef.current;
      const menu = menuRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const estimatedDropdownHeight = menu?.offsetHeight || 300;
      const shouldFlip = spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow;
      setIsFlipped(shouldFlip);
    };
    updateFlip();
    window.addEventListener("resize", updateFlip);
    window.addEventListener("scroll", updateFlip, true);
    return () => {
      window.removeEventListener("resize", updateFlip);
      window.removeEventListener("scroll", updateFlip, true);
    };
  }, [usePortal, isOpen]);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* Main dropdown button */}
      <div
        className={`
          relative ${fitContent ? "w-auto" : "w-full"} px-3 py-2 border rounded-lg cursor-pointer
          bg-white dark:bg-gray-700 transition-colors duration-200
          ${
            isDisabled
              ? "bg-gray-500 cursor-not-allowed border-gray-300 text-gray-900 placeholder-gray-400"
              : "hover:border-purple-400 dark:hover:border-purple-500"
          }
          ${isOpen ? "border-purple-500 ring-2 ring-purple-200 dark:ring-purple-800" : ""}
          ${error ? "border-red-500" : "border-gray-300 dark:border-gray-600"}
        `}
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <div
            className={`${
              fitContent ? "text-left whitespace-nowrap" : "flex-1 truncate text-left"
            }`}
          >
            {type === "multi" && selectedValues.length > 0 && !hideChips ? (
              <div className="flex flex-wrap gap-1">
                {selectedValues.slice(0, 2).map((val) => {
                  const item = items.find((i) => i.value === val);
                  return item ? (
                    <span
                      key={val}
                      className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-100 text-purple-800"
                    >
                      {item.label}
                      <button
                        type="button"
                        onClick={(e) => handleRemoveItem(val, e)}
                        className="ml-1 hover:text-purple-900"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </span>
                  ) : null;
                })}
                {selectedValues.length > 2 && (
                  <span className="inline-flex items-center px-2 py-0.5 text-xs text-gray-600">
                    +{selectedValues.length - 2} more
                  </span>
                )}
              </div>
            ) : type === "single" && selectedSingleItem ? (
              <span
                className={`inline-flex items-center gap-2 ${
                  isDisabled
                    ? "text-gray-500 dark:text-gray-600"
                    : "text-gray-900 dark:text-white"
                } text-sm`}
              >
                {selectedSingleItem.icon && (
                  <span className="inline-flex items-center">
                    {selectedSingleItem.icon}
                  </span>
                )}
                <span>{selectedSingleItem.label}</span>
              </span>
            ) : (
              <span
                className={`${
                  isDisabled
                    ? "text-gray-500 dark:text-gray-600"
                    : selectedValues.length === 0
                    ? "text-gray-500 dark:text-gray-400"
                    : "text-gray-900 dark:text-white"
                } text-sm`}
              >
                {getDisplayText()}
              </span>
            )}
          </div>

          <div className="flex items-center ml-2 space-x-1">
            {selectedValues.length > 0 && !isDisabled && showClear && (
              <button
                type="button"
                onClick={handleClearAll}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition-colors"
              >
                <XMarkIcon className="h-4 w-4 text-gray-500" />
              </button>
            )}
            {loading ? (
              <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-purple-600 border-t-transparent" />
            ) : (
              <ChevronDownIcon
                className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                  isOpen ? "transform rotate-180" : ""
                }`}
              />
            )}
          </div>
        </div>
      </div>

      {/* Dropdown menu */}
      {isOpen &&
        !isDisabled &&
        (!usePortal ? (
          <div
            ref={menuRef}
            className={`absolute z-50 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg ${
              isFlipped ? 'bottom-full mb-1' : 'mt-1'
            }`}
          >
            {/* Search input */}
            {searchable && (
              <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    autoFocus
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder={resolvedSearchPlaceholder}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            )}

            {/* Options list */}
            <div className="overflow-y-auto" style={{ maxHeight }}>
              {filteredItems.length === 0 ? (
                creatable && searchTerm.trim() ? (
                  <div
                    onClick={handleCreateItem}
                    className="px-3 py-2 cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors duration-150"
                  >
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span className="text-sm text-purple-600 font-medium">
                        Create "{searchTerm.trim()}"
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                    No items found
                  </div>
                )
              ) : (
                <>
                  {filteredItems.map((item, index) => {
                    const isSelected = selectedValues.includes(item.value);
                    const isHighlighted = index === highlightedIndex;
                    return (
                      <div
                        key={item.value}
                        onClick={() => handleItemClick(item)}
                        className={`
                        px-3 py-2 transition-colors duration-150
                        ${item.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
                        ${isSelected ? "bg-purple-50 dark:bg-purple-900/20" : ""}
                        ${isHighlighted ? "bg-gray-100 dark:bg-gray-700" : !isSelected && !item.disabled && "hover:bg-gray-50 dark:hover:bg-gray-700/50"}
                      `}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 text-left">
                            <div
                              className={`text-sm text-left ${
                                isSelected
                                  ? "text-purple-900 dark:text-purple-300 font-medium"
                                  : "text-gray-900 dark:text-white"
                              }`}
                            >
                              <span className="inline-flex items-center gap-2">
                                {item.icon && (
                                  <span className="inline-flex items-center">
                                    {item.icon}
                                  </span>
                                )}
                                <span>{item.label}</span>
                              </span>
                            </div>
                            {item.subLabel && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 text-left">
                                {item.subLabel}
                              </div>
                            )}
                          </div>
                          {isSelected && (
                            <CheckIcon className="h-4 w-4 text-purple-600 ml-2 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {/* Create option when there are results but search term doesn't match exactly */}
                  {creatable && searchTerm.trim() && !filteredItems.some(item => item.label.toLowerCase() === searchTerm.trim().toLowerCase()) && (
                    <div
                      onClick={handleCreateItem}
                      className="px-3 py-2 cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors duration-150 border-t border-gray-100 dark:border-gray-700"
                    >
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span className="text-sm text-purple-600 font-medium">
                          Create "{searchTerm.trim()}"
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Select all / Clear all for multi-select */}
            {type === "multi" && filteredItems.length > 0 && (
              <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 flex justify-between">
                <button
                  type="button"
                  onClick={() => {
                    const allValues = filteredItems.map((item) => item.value);
                    onChange(allValues);
                  }}
                  className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-xs text-gray-600 hover:text-gray-700 font-medium"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        ) : (
          portalStyle &&
          createPortal(
            <div
              ref={menuRef}
              className="z-[9999] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg"
              style={{
                position: "fixed",
                ...(portalStyle.top !== undefined ? { top: portalStyle.top } : {}),
                ...(portalStyle.bottom !== undefined ? { bottom: portalStyle.bottom } : {}),
                left: portalStyle.left,
                width: portalStyle.width,
              }}
            >
              {searchable && (
                <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      autoFocus
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      placeholder={resolvedSearchPlaceholder}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              )}
              <div className="overflow-y-auto" style={{ maxHeight: portalStyle.maxHeight || maxHeight }}>
                {filteredItems.length === 0 ? (
                  creatable && searchTerm.trim() ? (
                    <div
                      onClick={handleCreateItem}
                      className="px-3 py-2 cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors duration-150"
                    >
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span className="text-sm text-purple-600 font-medium">
                          Create "{searchTerm.trim()}"
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                      No items found
                    </div>
                  )
                ) : (
                  <>
                    {filteredItems.map((item, index) => {
                      const isSelected = selectedValues.includes(item.value);
                      const isHighlighted = index === highlightedIndex;
                      return (
                        <div
                          key={item.value}
                          onClick={() => handleItemClick(item)}
                          className={`px-3 py-2 transition-colors duration-150 ${
                            item.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                          } ${
                            isSelected ? "bg-purple-50 dark:bg-purple-900/20" : ""
                          } ${
                            isHighlighted ? "bg-gray-100 dark:bg-gray-700" : !isSelected && !item.disabled && "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 text-left">
                              <div
                                className={`text-sm text-left ${
                                  isSelected
                                    ? "text-purple-900 dark:text-purple-300 font-medium"
                                    : "text-gray-900 dark:text-white"
                                }`}
                              >
                                <span className="inline-flex items-center gap-2">
                                  {item.icon && (
                                    <span className="inline-flex items-center">
                                      {item.icon}
                                    </span>
                                  )}
                                  <span>{item.label}</span>
                                </span>
                              </div>
                              {item.subLabel && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 text-left">
                                  {item.subLabel}
                                </div>
                              )}
                            </div>
                            {isSelected && (
                              <CheckIcon className="h-4 w-4 text-purple-600 ml-2 flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {/* Create option when there are results but search term doesn't match exactly */}
                    {creatable && searchTerm.trim() && !filteredItems.some(item => item.label.toLowerCase() === searchTerm.trim().toLowerCase()) && (
                      <div
                        onClick={handleCreateItem}
                        className="px-3 py-2 cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors duration-150 border-t border-gray-100 dark:border-gray-700"
                      >
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          <span className="text-sm text-purple-600 font-medium">
                            Create "{searchTerm.trim()}"
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              {type === "multi" && filteredItems.length > 0 && (
                <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 flex justify-between">
                  <button
                    type="button"
                    onClick={() => {
                      const allValues = filteredItems.map((item) => item.value);
                      onChange(allValues);
                    }}
                    className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => onChange([])}
                    className="text-xs text-gray-600 hover:text-gray-700 font-medium"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>,
            document.body
          )
        ))}

      {/* Error message */}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default Dropdown;
